/**
 * Task sorting shared by the Kanban board and the list view (FRIDAY-75).
 *
 * One comparator, one set of keys, so the "Sort by" control in both views
 * means the same thing:
 *
 *   - `manual` — the order the board stores (the column position).
 *   - `key`, `title`, `status`, `priority`, `assignee`, `points`, `due`,
 *     `milestone`, `sprint`, `start`, `labels` — the list view's column fields.
 *
 * `assignee` is the "issuer": the task is sorted by the assignee's display
 * name, not by their user id. `status` follows the board order of the
 * columns, `sprint` follows the sprint names with the backlog last. `labels`
 * sorts on the alphabetically first label name, unlabelled tasks last
 * (FRIDAY-105).
 */

import type { Task } from '@friday/shared/realtime'
import { compareTaskKeys, type ListField } from '@friday/shared/listFields'

export type TaskSortKey = ListField | 'manual'
export type SortDirection = 'asc' | 'desc'

/** The sort a board is showing: which key, and in which direction. */
export interface BoardSort {
  key: TaskSortKey
  direction: SortDirection
}

export const DEFAULT_BOARD_SORT: BoardSort = { key: 'manual', direction: 'asc' }

/** The keys the kanban board can sort on (FRIDAY-75). */
export const BOARD_SORT_OPTIONS: { value: TaskSortKey; label: string }[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'assignee', label: 'Issuer' },
  { value: 'due', label: 'Due date' },
  { value: 'priority', label: 'Priority' },
  { value: 'labels', label: 'Label' },
]

export const PRIORITY_RANK = { urgent: 0, high: 1, medium: 2, low: 3 } as const

/** What a comparator needs to know about the board around the task. */
export interface SortContext {
  /** Display name of a user id — the issuer of a task. */
  nameOf: (userId: string) => string
  /** Board order of a column id (0 for an unknown column). */
  columnRank: (columnId: string) => number
  /** Sprint name of a sprint id — null means the backlog. */
  sprintName: (sprintId: string | null) => string | null
  /** Milestone name of a milestone id — null means no milestone. */
  milestoneName: (milestoneId: string | null) => string | null
}

/** Empty values sort last in ascending order. */
function compareNullable<T>(
  left: T | null,
  right: T | null,
  compare: (a: T, b: T) => number,
): number {
  if (left === null && right === null) return 0
  if (left === null) return 1
  if (right === null) return -1
  return compare(left, right)
}

const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' })

/** The alphabetically first label name of a task, or null without labels. */
function firstLabel(task: Task): string | null {
  const names = (task.labels ?? []).map((l) => l.name)
  return names.length ? names.sort(byName)[0]! : null
}

/**
 * Compare two tasks on one sort key. Returns a negative number when `a`
 * comes first. Equal keys fall back to the task key, so the order is stable
 * and never depends on the order the board happened to arrive in.
 */
export function compareTasks(a: Task, b: Task, key: TaskSortKey, ctx: SortContext): number {
  let cmp = 0
  switch (key) {
    case 'manual':
      cmp = a.position - b.position
      break
    case 'key':
      cmp = compareTaskKeys(a, b)
      break
    case 'title':
      cmp = byName(a.title.toLowerCase(), b.title.toLowerCase())
      break
    case 'status':
      cmp = ctx.columnRank(a.columnId) - ctx.columnRank(b.columnId)
      break
    case 'priority':
      cmp = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]
      break
    case 'assignee':
      cmp = compareNullable(a.assigneeUserId, b.assigneeUserId, (left, right) =>
        byName(ctx.nameOf(left), ctx.nameOf(right)),
      )
      break
    case 'points':
      cmp = compareNullable(a.storyPoints, b.storyPoints, (left, right) => left - right)
      break
    case 'due':
      cmp = compareNullable(a.dueDate, b.dueDate, (left, right) =>
        new Date(left).getTime() - new Date(right).getTime(),
      )
      break
    case 'milestone':
      cmp = compareNullable(a.milestoneId, b.milestoneId, (left, right) =>
        byName(ctx.milestoneName(left) ?? '', ctx.milestoneName(right) ?? ''),
      )
      break
    case 'sprint':
      cmp = compareNullable(a.sprintId, b.sprintId, (left, right) =>
        byName(ctx.sprintName(left) ?? '', ctx.sprintName(right) ?? ''),
      )
      break
    case 'start':
      cmp = compareNullable(a.startDate, b.startDate, (left, right) =>
        new Date(left).getTime() - new Date(right).getTime(),
      )
      break
    case 'labels':
      cmp = compareNullable(firstLabel(a), firstLabel(b), byName)
      break
  }
  if (cmp !== 0) return cmp
  // Same key value — keep a deterministic order.
  return compareTaskKeys(a, b)
}

/** Sort a list of tasks on one key. The input array is not modified. */
export function sortTasks<T extends Task>(
  tasks: readonly T[],
  key: TaskSortKey,
  direction: SortDirection,
  ctx: SortContext,
): T[] {
  const sign = direction === 'asc' ? 1 : -1
  return [...tasks].sort((a, b) => compareTasks(a, b, key, ctx) * sign)
}
